/**
 * Admin Panel - Rate Limits Management API
 *
 * Zod schemas for organization rate limit management endpoints.
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../common/response.schema";

const SubscriptionTypeSchema = z.enum(DbEnum.SUBSCRIPTION_TYPE_VALUES);

/**
 * The per-tier workflow-node allowance the engine enforces.
 *
 * Built from `SUBSCRIPTION_TYPE_VALUES`, so it is exhaustive over
 * `SubscriptionType` by construction — a new tier is a compile error here and
 * at the backend table this mirrors
 * (`_common/subscription-tiers/workflow-node-tier-limits.ts`), rather than a
 * key the admin panel silently omits from its legend.
 */
export const TierDefaultsSchema = z.object(
  Object.fromEntries(DbEnum.SUBSCRIPTION_TYPE_VALUES.map((tier) => [tier, z.number()])) as Record<
    (typeof DbEnum.SUBSCRIPTION_TYPE_VALUES)[number],
    z.ZodNumber
  >
);

// ============================================================
// Schemas
// ============================================================

/**
 * Current usage statistics for rate limiting
 */
export const CurrentUsageSchema = z.object({
  nodesInWindow: z.number(),
  queueSize: z.number()
});

/**
 * Rate limit statistics for a single organization
 */
export const RateLimitStatsSchema = z.object({
  // Organization id is the Clerk org id (e.g. "org_2abc..."), not a UUID.
  organizationId: z.string(),
  organizationName: z.string(),
  subscriptionType: SubscriptionTypeSchema.nullable(),
  defaultRateLimit: z.number(),
  customRateLimit: z.number().nullable(),
  effectiveRateLimit: z.number(),
  currentUsage: CurrentUsageSchema.optional()
});

/**
 * Detailed rate limit stats (includes current usage)
 */
export const RateLimitDetailedStatsSchema = RateLimitStatsSchema.extend({
  currentUsage: CurrentUsageSchema
});

/**
 * List rate limits response
 */
export const ListRateLimitsResponseSchema = z.object({
  organizations: z.array(RateLimitStatsSchema),
  total: z.number(),
  /**
   * The tier allowance table itself, so the admin legend RENDERS the numbers
   * the engine enforces instead of carrying its own copy of them.
   *
   * OPTIONAL because of deploy ORDER, not because the backend may omit it. The
   * generated hook runs `Response.parse(...)` on the client, so a frontend
   * carrying this contract against a backend that predates it would fail
   * validation and take the whole rate-limits page down — not just the legend.
   * The frontend and the backend are separate Porter apps and there is a window
   * where exactly that pairing is live. `TierLegend` renders nothing until both
   * fields arrive, which is the correct degradation: a legend that invents
   * numbers is the defect this whole change removes.
   */
  tierDefaults: TierDefaultsSchema.optional(),
  /** Allowance for an organization with no live subscription row. */
  unsubscribedDefault: z.number().optional()
});

/**
 * List rate limits query params
 */
export const ListRateLimitsParamsSchema = z.object({
  search: z.string().optional(),
  limit: z.coerce.number().optional().default(50),
  offset: z.coerce.number().optional().default(0)
});

/**
 * Organization path variable
 */
export const OrganizationPathVarsSchema = z.object({
  // Organization id is the Clerk org id (e.g. "org_2abc..."), not a UUID.
  organizationId: z.string()
});

/**
 * Set rate limit request body
 */
export const SetRateLimitBodySchema = z.object({
  /**
   * The per-organization override, or `null` to clear it and fall back to the
   * tier default.
   *
   * `.int().positive()` is load-bearing. This was a bare `z.number()`, so an
   * admin could store `0` — and the two readers disagreed about what that meant:
   * the engine tested it for truthiness and ran the organization at its tier
   * default, while the admin panel coalesced on nullish and reported a hard stop
   * of zero. Refusing the value is what stops that row existing; a stored zero
   * from before this is read as "no override" by both sides.
   *
   * The use case also refuses a non-positive value, which produced a 500. This
   * makes it a 400 naming the field.
   */
  customRateLimitNodes: z.number().int().positive().nullable()
});

/**
 * Set rate limit response
 */
export const SetRateLimitResponseSchema = z.object({
  success: z.boolean(),
  effectiveRateLimit: z.number()
});

/**
 * Reset rate limit response
 */
export const ResetRateLimitResponseSchema = z.object({
  success: z.boolean(),
  effectiveRateLimit: z.number()
});

// ============================================================
// ZAdminRateLimits - Endpoint Definitions
// ============================================================

export const ZAdminRateLimits = {
  /**
   * List rate limits for all organizations
   * GET /api/admin/rate-limits
   */
  ListRateLimits: {
    method: "GET" as const,
    path: "/api/admin/rate-limits",
    Params: ListRateLimitsParamsSchema,
    Response: ApiSuccessSchema(ListRateLimitsResponseSchema)
  },

  /**
   * Get rate limit details for a specific organization
   * GET /api/admin/rate-limits/:organizationId
   */
  GetOrganizationRateLimit: {
    method: "GET" as const,
    path: "/api/admin/rate-limits/:organizationId",
    PathVars: OrganizationPathVarsSchema,
    Response: ApiSuccessSchema(RateLimitDetailedStatsSchema)
  },

  /**
   * Set custom rate limit for an organization
   * POST /api/admin/rate-limits/:organizationId
   */
  SetOrganizationRateLimit: {
    method: "POST" as const,
    path: "/api/admin/rate-limits/:organizationId",
    PathVars: OrganizationPathVarsSchema,
    Body: SetRateLimitBodySchema,
    Response: ApiSuccessSchema(SetRateLimitResponseSchema)
  },

  /**
   * Reset organization rate limit to tier default
   * POST /api/admin/rate-limits/:organizationId/reset
   */
  ResetOrganizationRateLimit: {
    method: "POST" as const,
    path: "/api/admin/rate-limits/:organizationId/reset",
    PathVars: OrganizationPathVarsSchema,
    Response: ApiSuccessSchema(ResetRateLimitResponseSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type CurrentUsage = z.infer<typeof CurrentUsageSchema>;
export type RateLimitStats = z.infer<typeof RateLimitStatsSchema>;
export type RateLimitDetailedStats = z.infer<typeof RateLimitDetailedStatsSchema>;
export type ListRateLimitsResponse = z.infer<typeof ListRateLimitsResponseSchema>;
export type SetRateLimitBody = z.infer<typeof SetRateLimitBodySchema>;
export type SetRateLimitResponse = z.infer<typeof SetRateLimitResponseSchema>;
export type ResetRateLimitResponse = z.infer<typeof ResetRateLimitResponseSchema>;
